/**
 * The style cascade (docs/CIRCLES.md §5) and the prominence arithmetic that
 * rides along with it (§4). Pure: no DOM, no fetching of anything.
 *
 * ```
 * app defaults → theme → calendar → tag path (root … leaf) → source → event
 * ```
 *
 * Later wins, and an absent token means *inherit* rather than "unset", so it
 * falls through to whatever the level below decided. Two things do not behave
 * that way. `prominence` is a weight, not a choice, so every level's value
 * MULTIPLIES the ones before it: a circle at 1.4 holding a person at 0.5 is at
 * 0.7, and nothing on the way down can silently discard the levels above it.
 * And a source's `colour` is a *default* for what it brings in, not an override
 * of the circle it was filed into: it applies only where no circle on the path
 * has a colour, so a feed default-tagged into Work lets Work's slate show
 * through rather than repainting every entry in the feed's own colour. The
 * source's other tokens cascade like anyone else's.
 */

import type { ResolvedStyle, StyleTokens } from './types';

/**
 * Bounds on the *product*. Per level the range is [0.25, 4] (the schema says
 * so); over a path of depth 3 plus an event that compounds to [0.0039, 256],
 * which is more range than any renderer wants. Clamping at [0.05, 64] keeps a
 * de-emphasised circle legible and an emphasised one on the screen.
 */
export const PROMINENCE_MIN = 0.05;
export const PROMINENCE_MAX = 64;

/** The levels of the cascade, outermost first. `tagPath` runs root … leaf. */
export interface StyleCascade {
  /** What the app falls back to. The only level that must be complete. */
  defaults: ResolvedStyle;
  theme?: StyleTokens;
  calendar?: StyleTokens;
  /** One entry per tag from the root down to the leaf; empty for an untagged event. */
  tagPath: StyleTokens[];
  source?: StyleTokens;
  event?: StyleTokens;
}

function clampProminence(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(PROMINENCE_MAX, Math.max(PROMINENCE_MIN, value));
}

/** A level's own weight. Absent, unusable or non-positive counts as "no opinion". */
function weightOf(tokens: StyleTokens | undefined): number {
  const p = tokens?.prominence;
  return typeof p === 'number' && Number.isFinite(p) && p > 0 ? p : 1;
}

/**
 * Collapse the cascade for one event on one ring. `tagPath` is the path of the
 * ring the event is being drawn on; on a merged ring that is the path of its
 * highest-prominence tag (CIRCLES.md §5).
 *
 * The returned `prominence` is the product of every level, clamped — it is the
 * effective weight, not any one level's setting.
 */
export function resolveStyle(input: StyleCascade): ResolvedStyle {
  const { defaults } = input;
  const tagPath = input.tagPath ?? [];
  const levels: Array<StyleTokens | undefined> = [
    input.theme,
    input.calendar,
    ...tagPath,
    sourceLevel(input.source, tagPath),
    input.event,
  ];

  const out: ResolvedStyle = {
    colour: defaults.colour,
    ringMark: defaults.ringMark,
    float: defaults.float,
    opacity: defaults.opacity,
    labelFormat: defaults.labelFormat,
    avatarShape: defaults.avatarShape,
    prominence: defaults.prominence,
  };

  // The defaults are the first factor, not a floor: normally 1, so normally a no-op.
  let prominence = weightOf(defaults);

  for (const level of levels) {
    if (!level) continue;
    if (level.colour !== undefined) out.colour = level.colour;
    if (level.ringMark !== undefined) out.ringMark = level.ringMark;
    if (level.float !== undefined) out.float = level.float;
    if (level.opacity !== undefined) out.opacity = level.opacity;
    if (level.labelFormat !== undefined) out.labelFormat = level.labelFormat;
    if (level.avatarShape !== undefined) out.avatarShape = level.avatarShape;
    prominence *= weightOf(level);
  }

  out.prominence = clampProminence(prominence);
  return out;
}

/** The source's level as the cascade reads it: colourless once a circle on the path has spoken. */
function sourceLevel(
  source: StyleTokens | undefined,
  tagPath: readonly (StyleTokens | undefined)[],
): StyleTokens | undefined {
  if (!source || source.colour === undefined) return source;
  if (!tagPath.some((level) => level?.colour !== undefined)) return source;
  const { colour: _yielded, ...rest } = source;
  return rest;
}

/**
 * `Π path prominence × event.prominence`, clamped the same way the cascade
 * clamps it. Feeds band weight, concentric radius order, split ring size, mark
 * and float size, and the float cluster priority.
 */
export function effectiveProminence(pathProminences: number[], eventProminence = 1): number {
  let product = 1;
  for (const p of pathProminences ?? []) {
    if (typeof p === 'number' && Number.isFinite(p) && p > 0) product *= p;
  }
  if (Number.isFinite(eventProminence) && eventProminence > 0) product *= eventProminence;
  return clampProminence(product);
}

/**
 * How opaque a thing may get to be at `effective` prominence: `0.6 + 0.4·eff`
 * over [0, 1], flat at 1 above it. A de-emphasised circle recedes; it never
 * vanishes, because a circle you cannot see is one you cannot turn back on.
 */
export function opacityFloor(effective: number): number {
  const e = Number.isFinite(effective) ? Math.min(1, Math.max(0, effective)) : 0;
  return 0.6 + 0.4 * e;
}
